import DOMPurify from 'dompurify';
import CryptoJS from 'crypto-js';

// Security configuration
const SECURITY_CONFIG = {
  // DOMPurify configuration for user content
  SANITIZE_CONFIG: {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'a'],
    ALLOWED_ATTR: ['href', 'target'],
    ALLOW_DATA_ATTR: false,
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed'],
    FORBID_ATTR: ['onclick', 'onload', 'onerror', 'onmouseover']
  },
  
  // Encryption key for localStorage (in production, use environment variable)
  ENCRYPTION_KEY: process.env.REACT_APP_ENCRYPTION_KEY || 'chirpx-default-key-change-in-production',
  
  // Rate limiting configuration
  RATE_LIMITS: {
    POST_CREATION: { maxRequests: 10, windowMs: 60000 }, // 10 posts per minute
    PROFILE_UPDATE: { maxRequests: 5, windowMs: 300000 }, // 5 updates per 5 minutes
    WALLET_CONNECT: { maxRequests: 20, windowMs: 60000 } // 20 connections per minute
  }
};

/**
 * Sanitize user input to prevent XSS attacks
 * @param {string} input - Raw user input
 * @param {Object} config - Custom DOMPurify configuration
 * @returns {string} - Sanitized content
 */
export const sanitizeInput = (input, config = SECURITY_CONFIG.SANITIZE_CONFIG) => {
  if (!input || typeof input !== 'string') {
    return '';
  }
  
  try {
    return DOMPurify.sanitize(input, config);
  } catch (error) {
    console.error('Input sanitization failed:', error);
    // Return empty string on sanitization failure for security
    return '';
  }
};

/**
 * Sanitize post content specifically
 * @param {string} content - Post content
 * @returns {string} - Sanitized post content
 */
export const sanitizePostContent = (content) => {
  const postConfig = {
    ...SECURITY_CONFIG.SANITIZE_CONFIG,
    // Allow line breaks and basic formatting for posts
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em'],
    ALLOWED_ATTR: []
  };
  
  return sanitizeInput(content, postConfig);
};

/**
 * Validate and sanitize profile data
 * @param {Object} profileData - Profile data object
 * @returns {Object} - Sanitized profile data
 */
export const sanitizeProfileData = (profileData) => {
  if (!profileData || typeof profileData !== 'object') {
    throw new Error('Invalid profile data');
  }
  
  const sanitized = {};
  
  // Sanitize username (priority field)
  if (profileData.username) {
    sanitized.username = sanitizeInput(profileData.username, {
      ALLOWED_TAGS: [],
      ALLOWED_ATTR: []
    });
    
    // Validate username length and characters
    if (sanitized.username.length > 30) {
      throw new Error('Username too long (max 30 characters)');
    }
    
    if (!/^[a-zA-Z0-9._-]+$/.test(sanitized.username)) {
      throw new Error('Username contains invalid characters (only letters, numbers, dots, underscores, and hyphens allowed)');
    }
  }
  
  // Sanitize display name (fallback or additional field)
  if (profileData.displayName) {
    sanitized.displayName = sanitizeInput(profileData.displayName, {
      ALLOWED_TAGS: [],
      ALLOWED_ATTR: []
    });
    
    // Validate display name length and characters
    if (sanitized.displayName.length > 50) {
      throw new Error('Display name too long (max 50 characters)');
    }
    
    if (!/^[a-zA-Z0-9\s._-]+$/.test(sanitized.displayName)) {
      throw new Error('Display name contains invalid characters');
    }
  }
  
  // Sanitize bio
  if (profileData.bio) {
    sanitized.bio = sanitizeInput(profileData.bio, {
      ALLOWED_TAGS: [],
      ALLOWED_ATTR: []
    });
    
    if (sanitized.bio.length > 500) {
      throw new Error('Bio too long (max 500 characters)');
    }
  }
  
  // Validate avatar URL
  if (profileData.avatarUrl) {
    try {
      const url = new URL(profileData.avatarUrl);
      if (!['http:', 'https:'].includes(url.protocol)) {
        throw new Error('Invalid avatar URL protocol');
      }
      sanitized.avatarUrl = profileData.avatarUrl;
    } catch (error) {
      throw new Error('Invalid avatar URL format');
    }
  }
  
  return sanitized;
};

/**
 * Encrypt sensitive data for localStorage
 * @param {string} data - Data to encrypt
 * @returns {string} - Encrypted data
 */
export const encryptData = (data) => {
  try {
    if (!data) return '';
    const encrypted = CryptoJS.AES.encrypt(JSON.stringify(data), SECURITY_CONFIG.ENCRYPTION_KEY).toString();
    return encrypted;
  } catch (error) {
    console.error('Encryption failed:', error);
    throw new Error('Failed to encrypt data');
  }
};

/**
 * Decrypt sensitive data from localStorage
 * @param {string} encryptedData - Encrypted data
 * @returns {any} - Decrypted data
 */
export const decryptData = (encryptedData) => {
  try {
    if (!encryptedData) return null;
    const bytes = CryptoJS.AES.decrypt(encryptedData, SECURITY_CONFIG.ENCRYPTION_KEY);
    const decrypted = bytes.toString(CryptoJS.enc.Utf8);
    return JSON.parse(decrypted);
  } catch (error) {
    console.error('Decryption failed:', error);
    return null;
  }
};

/**
 * Secure localStorage wrapper with encryption
 */
const SECURE_PREFIX = 'znoospace_secure_';
const LEGACY_PREFIX = 'chirpx_secure_';

export const secureStorage = {
  setItem: (key, value) => {
    try {
      const encrypted = encryptData(value);
      localStorage.setItem(`${SECURE_PREFIX}${key}`, encrypted);
    } catch (error) {
      console.error('Secure storage set failed:', error);
    }
  },
  
  getItem: (key) => {
    try {
      // Try new prefix first, fall back to legacy prefix for migration
      let encrypted = localStorage.getItem(`${SECURE_PREFIX}${key}`);
      if (!encrypted) {
        encrypted = localStorage.getItem(`${LEGACY_PREFIX}${key}`);
        if (encrypted) {
          // Migrate to new prefix
          localStorage.setItem(`${SECURE_PREFIX}${key}`, encrypted);
          localStorage.removeItem(`${LEGACY_PREFIX}${key}`);
        }
      }
      return decryptData(encrypted);
    } catch (error) {
      console.error('Secure storage get failed:', error);
      return null;
    }
  },
  
  removeItem: (key) => {
    localStorage.removeItem(`${SECURE_PREFIX}${key}`);
    localStorage.removeItem(`${LEGACY_PREFIX}${key}`);
  },
  
  clear: () => {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith(SECURE_PREFIX) || key.startsWith(LEGACY_PREFIX)) {
        localStorage.removeItem(key);
      }
    });
  }
};

/**
 * Validate Solana wallet address
 * @param {string} address - Wallet address to validate
 * @returns {boolean} - Whether address is valid
 */
export const validateWalletAddress = (address) => {
  if (!address || typeof address !== 'string') {
    return false;
  }
  
  // Solana addresses are base58 encoded and typically 32-44 characters
  const base58Regex = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;
  return base58Regex.test(address);
};

/**
 * Validate wallet connection and provider
 * @param {Object} wallet - Wallet object to validate
 * @returns {Object} - Validation result
 */
export const validateWalletProvider = (wallet) => {
  const result = {
    isValid: false,
    errors: []
  };
  
  if (!wallet) {
    result.errors.push('Wallet object is null or undefined');
    return result;
  }
  
  // Check required properties
  if (!wallet.publicKey) {
    result.errors.push('Wallet missing publicKey');
  }
  
  if (typeof wallet.signTransaction !== 'function') {
    result.errors.push('Wallet missing signTransaction method');
  }
  
  if (typeof wallet.signAllTransactions !== 'function') {
    result.errors.push('Wallet missing signAllTransactions method');
  }
  
  // Validate public key format
  if (wallet.publicKey && !validateWalletAddress(wallet.publicKey.toString())) {
    result.errors.push('Invalid wallet address format');
  }
  
  result.isValid = result.errors.length === 0;
  return result;
};

/**
 * Rate limiting implementation
 */
class RateLimiter {
  constructor() {
    this.requests = new Map();
  }
  
  isAllowed(key, config) {
    const now = Date.now();
    const windowStart = now - config.windowMs;
    
    if (!this.requests.has(key)) {
      this.requests.set(key, []);
    }
    
    const userRequests = this.requests.get(key);
    
    // Remove old requests outside the window
    const validRequests = userRequests.filter(timestamp => timestamp > windowStart);
    this.requests.set(key, validRequests);
    
    // Check if under limit
    if (validRequests.length < config.maxRequests) {
      validRequests.push(now);
      return true;
    }
    
    return false;
  }
  
  getRemainingRequests(key, config) {
    const now = Date.now();
    const windowStart = now - config.windowMs;
    
    if (!this.requests.has(key)) {
      return config.maxRequests;
    }
    
    const userRequests = this.requests.get(key);
    const validRequests = userRequests.filter(timestamp => timestamp > windowStart);
    
    return Math.max(0, config.maxRequests - validRequests.length);
  }
}

export const rateLimiter = new RateLimiter();

/**
 * Check rate limit for specific action
 * @param {string} action - Action type (POST_CREATION, PROFILE_UPDATE, etc.)
 * @param {string} identifier - User identifier (wallet address)
 * @returns {Object} - Rate limit result
 */
export const checkRateLimit = (action, identifier) => {
  const config = SECURITY_CONFIG.RATE_LIMITS[action];
  if (!config) {
    return { allowed: true, remaining: Infinity };
  }
  
  const allowed = rateLimiter.isAllowed(identifier, config);
  const remaining = rateLimiter.getRemainingRequests(identifier, config);
  
  return { allowed, remaining, resetTime: Date.now() + config.windowMs };
};

/**
 * Sanitize error messages to prevent information disclosure
 * @param {Error} error - Original error
 * @returns {string} - Safe error message
 */
export const sanitizeErrorMessage = (error) => {
  if (!error) return 'An unknown error occurred';
  
  const message = error.message || error.toString();
  
  // Remove sensitive information patterns
  const sensitivePatterns = [
    /private key/gi,
    /secret/gi,
    /password/gi,
    /token/gi,
    /api[_\s]?key/gi,
    /wallet[_\s]?address/gi,
    /[0-9A-Za-z]{40,}/g, // Remove long strings that might be keys/addresses
  ];
  
  let sanitized = message;
  sensitivePatterns.forEach(pattern => {
    sanitized = sanitized.replace(pattern, '[REDACTED]');
  });
  
  // Limit message length
  if (sanitized.length > 200) {
    sanitized = sanitized.substring(0, 200) + '...';
  }
  
  return sanitized;
};

export default {
  sanitizeInput,
  sanitizePostContent,
  sanitizeProfileData,
  encryptData,
  decryptData,
  secureStorage,
  validateWalletAddress,
  validateWalletProvider,
  checkRateLimit,
  sanitizeErrorMessage
};
